import { ExtendedProperties } from "components/bid/form/partials";
import BookForm from "components/shared/book/form";
import InlineBtn from "components/shared/inlineBtn";
import SelectMenuActionButton from "components/shared/selectMenuActionButton";
import { comparePointsWithSegments } from "helpers/bid";
import { bidPointTypes, bidPointTypesEnum, simplifiedTypeCodesEnum } from "helpers/enums";
import { point } from "helpers/models";
import { enumLabel } from "helpers/system";
import policy from "policies";
import React from "react";
import { components } from "react-select";
import { getItems as cargoOwnersInfo } from "sources/books/cargoOwners";
import { index as counterpartyContracts } from "sources/books/counterpartyContracts";

export const Option = (props) => {
    const { name, debtCeiling, isMain } = props.data.option;
    return (
        <components.Option {...props}>
            {name}
            {debtCeiling != null && <small className="text-muted ml-1">(Доступный лимит: {debtCeiling})</small>}
            {isMain && <span className="badge badge-primary ml-1">Основной</span>}
        </components.Option>
    );
};

export const ValueContainer = (props) => {
    const { children, getValue, hasValue } = props;
    if (!hasValue) {
        return <components.ValueContainer {...props} />;
    }
    const value = getValue()[0];
    if (!value || !value.option) {
        return <components.ValueContainer {...props} />;
    }
    const { debtCeiling, isMain } = value.option;
    return (
        <components.ValueContainer {...props}>
            {!props.selectProps.inputValue && (
                <components.SingleValue {...props}>
                    <div>
                        {value.label}
                        {debtCeiling != null && (
                            <small className="text-muted ml-1">(Доступный лимит: {debtCeiling})</small>
                        )}
                        {isMain && <span className="badge badge-primary ml-1">Основной</span>}
                    </div>
                </components.SingleValue>
            )}
            {children[children.length - 1]}
        </components.ValueContainer>
    );
};

export const ContractMenuList = (props) => {
    const {
        children,
        selectProps: { counterpartyId, inputProps, legalPersonId, modal, organization, roles, onChange },
    } = props;
    const canCreate = React.useMemo(() => {
        return policy("user", {
            user: {
                canEditAll: !organization.noAccessToEditBooks,
                intBooksEditPermissionsFlags: organization.intBooksEditPermissionsFlags,
                roles,
            },
            record: {},
            options: { book: "cargoowners" },
        }).canEditBook();
    }, [organization, roles]);
    const canCreateContract = canCreate && counterpartyId && legalPersonId;
    const handleCreateContract = () => {
        if (modal) {
            modal({
                name: "counterpartyContractForm",
                title: "Создание договора",
                windowMod: "wide",
                counterpartyId,
                data: { legalPersonId },
                onSuccess: (contract) => {
                    if (!contract?.id) {
                        return;
                    }
                    onChange({
                        name: inputProps.name,
                        value: contract.id,
                        option: contract,
                    });
                },
            });
        }
    };

    return (
        <>
            <components.MenuList {...props}>{children}</components.MenuList>
            {canCreateContract && (
                <div className="text-center border-top">
                    <SelectMenuActionButton onAction={handleCreateContract}>Создать договор</SelectMenuActionButton>
                </div>
            )}
        </>
    );
};

export default class BaseBidForm extends BookForm {
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { match } = nextProps;
        if (this.props.match.params.id !== match.params.id) {
            return this.initForm(nextProps);
        }
    }

    updateCounterpartiesData = (id, data) => {
        const { counterpartiesData } = this.state;
        if (id && !counterpartiesData[id]) {
            counterpartiesData[id] = {
                hasPoints: data.hasPoints,
                name: data.name,
                id,
                comment: data.comment,
                status: data.status,
                insolvencyRegistryEntry: data.insolvencyRegistryEntry,
            };
            this.setState({ counterpartiesData });
        }
    };

    autoSelectCounterpartyContract = (counterpartyId, legalPersonId) => {
        if (!counterpartyId) {
            return this.setValue({ name: "contractId", value: undefined });
        }
        if (!this.autoSetMainContractEnabled || this.props.config.contractId.isHidden) {
            return;
        }
        const preparedFilter = legalPersonId ? { legalPersonId } : {};
        counterpartyContracts({ counterpartyId, preparedFilter, orderBy: "isMain desc", length: 1 }).then(
            ({ data, count }) => {
                const contract = data[0];
                if (contract?.isMain || count === 1) {
                    this.setValue({ name: "contractId", value: contract.id, option: contract });
                    if (!legalPersonId) {
                        this.setState({ legalPersonId: contract.legalPersonId });
                    }
                }
            },
        );
    };

    checkCounterpartyPoints = (bid) => {
        const counterpartyIdsForCheck = [];
        if (bid.cargoOwnerDictionaryItemId) {
            counterpartyIdsForCheck.push(bid.cargoOwnerDictionaryItemId);
        }
        if (bid.counterpartyId) {
            counterpartyIdsForCheck.push(bid.counterpartyId);
        }
        bid.bidPoints.forEach((point) => {
            if (point.cargoOwnerDictionaryItemId) {
                counterpartyIdsForCheck.push(point.cargoOwnerDictionaryItemId);
            }
            if (point.counterpartyId) {
                counterpartyIdsForCheck.push(point.counterpartyId);
            }
        });
        cargoOwnersInfo({ id: { in: counterpartyIdsForCheck } }).then((data) => {
            if (data && data.length > 0) {
                const counterpartiesData = data.reduce((acc, item) => {
                    acc[item.id] = {
                        hasPoints: item.hasPoints || item.points.length > 0,
                        name: item.name,
                        id: item.id,
                        comment: item.comment,
                        status: item.status,
                        insolvencyRegistryEntry: item.insolvencyRegistryEntry,
                    };
                    return acc;
                }, {});
                this.setState({ counterpartiesData });
            }
        });
    };

    failureAction = () => this.setState({ isLoading: false, isValid: false });

    renderExtendedProperties = () => {
        const { config } = this.props;
        return (
            <ExtendedProperties
                renderExtendedField={this.renderExtendedField}
                config={config.extendedProperties ? config.extendedProperties.config : {}}
                isEmptyMileageBid={this.state.isEmptyMileageBid}
                showAllInEmptyMileageBid={this.customFieldsEnabled}
            />
        );
    };

    setExtendedValue = ({ name, value }) => {
        const isValid = this.isValidForm({
            ...this.state,
            extendedProperties: { ...this.state.extendedProperties, [name]: value },
        });
        this.setState({
            extendedProperties: { ...this.state.extendedProperties, [name]: value },
            ...(isValid ? { isValid: true } : {}),
        });
    };

    renderExtendedField = (item) => {
        const value = this.state.extendedProperties[item.inputProps.name];
        const props = { ...item, onChange: this.setExtendedValue, value };
        switch (item.typeCode) {
            case simplifiedTypeCodesEnum.DateTime:
            case simplifiedTypeCodesEnum.DateTimeWithoutTimezone:
                return this.renderDateField(props);
            case simplifiedTypeCodesEnum.Boolean:
                return this.renderCheckbox(props);
            default:
                return this.renderField(props);
        }
    };

    addPoint = (type) => {
        const { bidPoints } = this.state;
        const lastPoint = bidPoints[bidPoints.length - 1];
        if (bidPointTypesEnum.CustomPoint === type) {
            bidPoints.splice(
                bidPoints.length - 1,
                1,
                { ...point(0), type, order: lastPoint.order, planLeaveDate: "" },
                { ...lastPoint, order: lastPoint.order + 1 },
            );
            this.setState({
                bidPoints,
                pointsNotComparedToSegments: comparePointsWithSegments(bidPoints, this.state.route, true),
            });
        } else {
            const newPoints = [...bidPoints, { ...point(0), type, order: lastPoint.order + 1 }];
            this.setState({
                bidPoints: newPoints,
                pointsNotComparedToSegments: comparePointsWithSegments(newPoints, this.state.route, true),
            });
        }
    };

    renderPointActions = () => {
        const actions = [
            {
                value: enumLabel(bidPointTypes, bidPointTypesEnum.LoadPoint),
                cname: "plus",
                param: bidPointTypesEnum.LoadPoint,
                action: this.addPoint,
            },
            {
                value: enumLabel(bidPointTypes, bidPointTypesEnum.UnloadPoint),
                cname: "plus",
                param: bidPointTypesEnum.UnloadPoint,
                action: this.addPoint,
            },
            {
                value: enumLabel(bidPointTypes, bidPointTypesEnum.CustomPoint),
                cname: "plus",
                param: bidPointTypesEnum.CustomPoint,
                action: this.addPoint,
            },
        ];
        return actions.map((item, i) => <InlineBtn key={i} {...item} />);
    };

    renderFooter = () => {
        return <div className="book-form__actions">{this.renderActions()}</div>;
    };
}
